import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, Upload, Pencil, X, Package, AlertTriangle, Clock, Zap, DollarSign, ShoppingBag, TrendingUp, Users, Mail, AlertCircle, Eye, ChevronUp, ChevronDown, Tag, Star, Layers, FileText } from "lucide-react";
import { useCategories } from "@/hooks/useCategories";

const API = process.env.REACT_APP_BACKEND_URL;

const CONDITIONS = ["New", "Modded", "Refurbished", "Like New", "Good", "Fair"];

function ProductForm({ product, onSave, onCancel, uploading, setUploading, categories }) {
  const CATEGORIES = categories.map(c => ({ value: c.key, label: c.label }));
  const [form, setForm] = useState(
    product || { name: "", description: "", price: "", category: "consoles", stock: "", condition: "Refurbished", shipping_cost: "", preorder_enabled: false, preorder_limit: 10, color_customizable: false }
  );
  const [imageUrls, setImageUrls] = useState(() => {
    if (product?.image_urls?.length) return product.image_urls;
    if (product?.image_url) return [product.image_url];
    return [];
  });
  const [pendingFiles, setPendingFiles] = useState([]); // {file, previewUrl}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState("");

  const totalImages = imageUrls.length + pendingFiles.length;

  const handleFilesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 10 - totalImages;
    if (remaining <= 0) { setError("Maximum 10 images per product"); return; }
    const toAdd = files.slice(0, remaining).map(f => ({ file: f, previewUrl: URL.createObjectURL(f) }));
    setPendingFiles(prev => [...prev, ...toAdd]);
    e.target.value = "";
  };

  const removeExisting = (idx) => setImageUrls(prev => prev.filter((_, i) => i !== idx));
  const removePending = (idx) => {
    setPendingFiles(prev => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      // Upload all pending files
      const uploadedUrls = [...imageUrls];
      if (pendingFiles.length > 0) {
        setUploading(true);
        for (let i = 0; i < pendingFiles.length; i++) {
          setUploadProgress(`Uploading image ${i + 1} of ${pendingFiles.length}...`);
          const formData = new FormData();
          formData.append("file", pendingFiles[i].file);
          try {
            const { data: uploadResult } = await axios.post(
              `${API}/api/upload/image`, formData,
              { headers: { "Content-Type": "multipart/form-data" } }
            );
            uploadedUrls.push(`/api/files/${uploadResult.path}`);
          } catch (uploadErr) {
            const msg = uploadErr.response?.data?.detail || uploadErr.message || "Unknown error";
            setError(`Image upload failed (image ${i + 1}): ${msg}`);
            setSaving(false);
            setUploading(false);
            setUploadProgress("");
            return;
          }
        }
        setUploading(false);
        setUploadProgress("");
      }

      const payload = {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        category: form.category,
        image_urls: uploadedUrls,
        image_url: uploadedUrls[0] || "",
        stock: parseInt(form.stock) || 0,
        condition: form.condition,
        shipping_cost: form.shipping_cost !== "" && form.shipping_cost !== null && form.shipping_cost !== undefined ? parseFloat(form.shipping_cost) : null,
        preorder_enabled: !!form.preorder_enabled,
        preorder_limit: parseInt(form.preorder_limit) || 10,
        color_customizable: !!form.color_customizable,
      };

      if (product?.id) {
        await axios.put(`${API}/api/products/${product.id}`, payload);
      } else {
        await axios.post(`${API}/api/products`, payload);
      }
      onSave();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg = "Failed to save product";
      if (status === 401) msg = "Session expired. Please log out and log back in.";
      else if (status === 403) msg = "Admin access required.";
      else if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(d => d.msg || JSON.stringify(d)).join(", ");
      else if (err.message) msg = `Network error: ${err.message}`;
      setError(msg);
    } finally {
      setSaving(false);
      setUploading(false);
      setUploadProgress("");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="product-form">
      {/* Multi-Image Upload */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">
            Product Images ({totalImages}/10)
          </Label>
          {totalImages < 10 && (
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] transition-colors font-mono text-xs uppercase tracking-wider" data-testid="upload-image-btn">
              <Upload className="h-3 w-3" />
              Add Images
              <input type="file" accept="image/*" multiple onChange={handleFilesSelect} className="hidden" />
            </label>
          )}
        </div>

        {/* Image Grid */}
        <div className="flex flex-wrap gap-2">
          {/* Existing uploaded images */}
          {imageUrls.map((url, idx) => (
            <div key={`existing-${idx}`} className="relative w-24 h-24 border border-[#1A1A1A] group overflow-hidden flex-shrink-0">
              <img src={url} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" />
              {idx === 0 && (
                <span className="absolute top-0 left-0 bg-[#39FF14] text-[#050505] font-mono text-[8px] font-bold px-1.5 py-0.5 uppercase">Main</span>
              )}
              <button
                type="button"
                onClick={() => removeExisting(idx)}
                className="absolute top-0 right-0 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${idx}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {/* Pending files (not yet uploaded) */}
          {pendingFiles.map((pf, idx) => (
            <div key={`pending-${idx}`} className="relative w-24 h-24 border border-[#39FF14]/30 border-dashed group overflow-hidden flex-shrink-0">
              <img src={pf.previewUrl} alt={`New ${idx + 1}`} className="w-full h-full object-cover opacity-70" />
              <span className="absolute bottom-0 left-0 right-0 bg-[#050505]/80 text-[#39FF14] font-mono text-[8px] text-center py-0.5">New</span>
              <button
                type="button"
                onClick={() => removePending(idx)}
                className="absolute top-0 right-0 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-pending-${idx}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {/* Add more placeholder */}
          {totalImages < 10 && totalImages > 0 && (
            <label className="w-24 h-24 border border-dashed border-[#1A1A1A] hover:border-[#39FF14] flex items-center justify-center cursor-pointer transition-colors flex-shrink-0">
              <Plus className="h-5 w-5 text-[#A1A1A1]" />
              <input type="file" accept="image/*" multiple onChange={handleFilesSelect} className="hidden" />
            </label>
          )}
        </div>
        {totalImages === 0 && (
          <p className="text-[#A1A1A1] text-xs">Upload up to 10 images. The first image will be the main product photo.</p>
        )}
        {uploadProgress && <p className="text-[#39FF14] text-xs font-mono">{uploadProgress}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Name</Label>
          <Input
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            required placeholder="Product name"
            className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]"
            data-testid="product-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]" data-testid="product-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0D0D0D] border-[#1A1A1A] rounded-none">
              {CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value} className="text-[#F2F2F2] focus:text-[#39FF14] focus:bg-[#141414] rounded-none">
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Description</Label>
        <Textarea
          value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          required placeholder="Product description"
          className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] min-h-[80px]"
          data-testid="product-description-input"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Price ($)</Label>
          <Input
            type="number" step="0.01" min="0" value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            required placeholder="0.00"
            className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]"
            data-testid="product-price-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Shipping ($)</Label>
          <Input
            type="number" step="0.01" min="0" value={form.shipping_cost ?? ""}
            onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })}
            placeholder="Auto"
            className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]"
            data-testid="product-shipping-input"
          />
          <p className="text-[#A1A1A1] text-[10px] font-mono">Leave blank for category default</p>
        </div>
        <div className="space-y-2">
          <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Stock</Label>
          <Input
            type="number" min="0" value={form.stock}
            onChange={(e) => setForm({ ...form, stock: e.target.value })}
            required placeholder="0"
            className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]"
            data-testid="product-stock-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Condition</Label>
          <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
            <SelectTrigger className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]" data-testid="product-condition-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0D0D0D] border-[#1A1A1A] rounded-none">
              {CONDITIONS.map(c => (
                <SelectItem key={c} value={c} className="text-[#F2F2F2] focus:text-[#39FF14] focus:bg-[#141414] rounded-none">
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Preorder Settings */}
      <div className="border border-[#1A1A1A] p-4 space-y-3">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={!!form.preorder_enabled}
            onChange={(e) => setForm({ ...form, preorder_enabled: e.target.checked })}
            id="preorder-toggle"
            data-testid="preorder-toggle"
            className="w-4 h-4 accent-[#39FF14]"
          />
          <Label htmlFor="preorder-toggle" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1] cursor-pointer">
            Enable Preorder (when out of stock)
          </Label>
        </div>
        {form.preorder_enabled && (
          <div className="flex items-center gap-3">
            <Label className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Limit</Label>
            <Input
              type="number" min="1" value={form.preorder_limit ?? 10}
              onChange={(e) => setForm({ ...form, preorder_limit: e.target.value })}
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] w-24"
              data-testid="preorder-limit-input"
            />
            <span className="text-[#A1A1A1] text-xs">max concurrent preorders</span>
          </div>
        )}
      </div>

      {/* Color Customization */}
      <div className="border border-[#1A1A1A] p-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={!!form.color_customizable}
            onChange={(e) => setForm({ ...form, color_customizable: e.target.checked })}
            id="color-toggle"
            data-testid="color-toggle"
            className="w-4 h-4 accent-[#39FF14]"
          />
          <Label htmlFor="color-toggle" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1] cursor-pointer">
            Allow Custom Color Selection (Red, White, Green, Blue, Yellow)
          </Label>
        </div>
      </div>

      {error && (
        <div className="p-4 border-2 border-red-500 bg-red-500/20 text-red-300 text-sm font-mono" data-testid="form-error">
          <p className="font-bold uppercase tracking-wider text-xs text-red-400 mb-1">Error</p>
          <p>{error}</p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button
          type="submit" disabled={saving || uploading}
          className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-6 py-5 disabled:opacity-50"
          data-testid="product-save-btn"
        >
          {uploading ? "Uploading..." : saving ? "Saving..." : product?.id ? "Update Product" : "Create Product"}
        </Button>
        <Button type="button" onClick={onCancel} variant="outline"
          className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase px-6 py-5"
          data-testid="product-cancel-btn"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function AdminDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const { categories } = useCategories();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const formRef = useRef(null);
  const [adminCategory, setAdminCategory] = useState("all");
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  const [initialAuthChecked, setInitialAuthChecked] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/products`);
      setProducts(data);
    } catch (e) {
      console.error("Failed to load products", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/metrics`);
      setMetrics(data);
    } catch (e) {
      console.error("Failed to load metrics", e);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!initialAuthChecked) {
      // Only redirect on first load if not admin
      if (!user || user.role !== "admin") {
        navigate("/");
        return;
      }
      setInitialAuthChecked(true);
    }
    fetchProducts();
    fetchMetrics();
  }, [user, authLoading, navigate, fetchProducts, fetchMetrics, initialAuthChecked]);

  const handleDelete = async (productId) => {
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.delete(`${API}/api/products/${productId}`);
      setPendingDelete(null);
      fetchProducts();
    } catch (e) {
      console.error("Delete failed", e);
      setDeleteError(`Delete failed: ${e.response?.data?.detail || e.message}. Status: ${e.response?.status || "network error"}`);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.delete(`${API}/api/products`);
      setPendingDelete(null);
      fetchProducts();
    } catch (e) {
      console.error("Delete all failed", e);
      setDeleteError(`Delete all failed: ${e.response?.data?.detail || e.message}. Status: ${e.response?.status || "network error"}`);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditProduct(null);
    fetchProducts();
  };

  const reorderProduct = async (categoryKey, productId, direction) => {
    // Get current category list in display order (matches grid render below)
    const list = products
      .filter(p => p.category === categoryKey)
      .sort((a, b) => {
        const aOrder = a.sort_order ?? 1_000_000;
        const bOrder = b.sort_order ?? 1_000_000;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
    const idx = list.findIndex(p => p.id === productId);
    const newIdx = idx + (direction === "up" ? -1 : 1);
    if (idx < 0 || newIdx < 0 || newIdx >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    const orderedIds = reordered.map(p => p.id);
    // Optimistic update
    setProducts(prev => prev.map(p => {
      if (p.category !== categoryKey) return p;
      const newOrder = orderedIds.indexOf(p.id);
      return newOrder >= 0 ? { ...p, sort_order: newOrder } : p;
    }));
    try {
      await axios.put(`${API}/api/admin/products/reorder`, { ordered_ids: orderedIds });
    } catch (e) {
      console.error("Reorder failed", e);
      fetchProducts(); // rollback
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Admin</p>
            <h1 data-testid="admin-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Product Manager
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate("/admin/orders")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-orders-btn"
            >
              <Package className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Manage Orders
            </Button>
            <Button
              onClick={() => navigate("/admin/waitlist")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-waitlist-btn"
            >
              <Clock className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Waitlist
            </Button>
            <Button
              onClick={() => navigate("/admin/preorders")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-preorders-btn"
            >
              <Zap className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Preorders
            </Button>
            <Button
              onClick={() => navigate("/admin/emails")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-emails-btn"
            >
              <AlertTriangle className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Messages
            </Button>
            <Button
              onClick={() => navigate("/admin/visitors")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-visitors-btn"
            >
              <Eye className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Visitors
            </Button>
            <Button
              onClick={() => navigate("/admin/categories")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-categories-btn"
            >
              <Layers className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Categories
            </Button>
            <Button
              onClick={() => navigate("/admin/site-content")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-site-content-btn"
            >
              <FileText className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Site Content
            </Button>
            <Button
              onClick={() => navigate("/admin/coupons")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-coupons-btn"
            >
              <Tag className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Coupons
            </Button>
            <Button
              onClick={() => navigate("/admin/reviews")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-reviews-btn"
            >
              <Star className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Reviews
            </Button>
            <Button
              onClick={() => navigate("/admin/users")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="go-to-users-btn"
            >
              <Users className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Users
            </Button>
            {products.length > 0 && !showForm && (
              pendingDelete === "ALL" ? (
                <div className="flex items-center gap-2 border border-red-600 bg-red-600/10 px-4 py-2">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <span className="text-red-400 font-mono text-xs">Delete all {products.length} products?</span>
                  <Button
                    onClick={handleDeleteAll}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-none font-mono uppercase text-xs font-bold px-4 py-2 h-auto ml-2"
                    data-testid="confirm-delete-all-btn"
                  >
                    {deleting ? "Deleting..." : "Yes, Delete All"}
                  </Button>
                  <Button
                    onClick={() => setPendingDelete(null)}
                    variant="outline"
                    className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#F2F2F2] rounded-none font-mono text-xs uppercase px-4 py-2 h-auto"
                    data-testid="cancel-delete-all-btn"
                  >
                    No
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setPendingDelete("ALL")}
                  variant="outline"
                  className="bg-transparent border-red-600/50 hover:border-red-500 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-none font-mono text-xs uppercase tracking-wider"
                  data-testid="delete-all-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Delete All
                </Button>
              )
            )}
            {!showForm && (
              <Button
                onClick={() => { setEditProduct(null); setShowForm(true); }}
                className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-6 py-5"
                data-testid="add-product-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Product
              </Button>
            )}
          </div>
        </div>

        {/* Metrics Dashboard */}
        {!metricsLoading && metrics && (
          <div className="mb-10" data-testid="admin-metrics">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14]">Snapshot</h2>
              <button
                onClick={fetchMetrics}
                className="font-mono uppercase tracking-wider text-[10px] text-[#A1A1A1] hover:text-[#39FF14] transition-colors"
                data-testid="metrics-refresh"
              >
                Refresh
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-[1px] bg-[#1A1A1A] mb-4">
              {[
                { label: "Revenue (week)", value: `$${metrics.revenue.week.toFixed(2)}`, sub: `$${metrics.revenue.lifetime.toFixed(2)} lifetime`, icon: DollarSign, testid: "metric-revenue-week" },
                { label: "Orders (week)", value: metrics.orders.week, sub: `${metrics.orders.lifetime} lifetime`, icon: ShoppingBag, testid: "metric-orders-week" },
                { label: "Active Preorders", value: metrics.preorders.active, sub: `$${metrics.revenue.preorder_lifetime.toFixed(2)} preorder rev`, icon: Zap, testid: "metric-preorders-active" },
                { label: "Waitlist", value: metrics.waitlist.total, sub: `${metrics.tickets.open} open ticket${metrics.tickets.open === 1 ? "" : "s"}`, icon: Users, testid: "metric-waitlist" },
              ].map(({ label, value, sub, icon: Icon, testid }) => (
                <div key={label} data-testid={testid} className="bg-[#0D0D0D] p-5 hover:bg-[#141414] transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">{label}</p>
                    <Icon className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
                  </div>
                  <p className="text-2xl sm:text-3xl font-black text-[#F2F2F2] mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {value}
                  </p>
                  <p className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider">{sub}</p>
                </div>
              ))}
            </div>

            {/* Secondary row: top products + low stock */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-[1px] bg-[#1A1A1A]">
              {/* Top Products */}
              <div className="bg-[#0D0D0D] p-5" data-testid="metric-top-products">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
                  <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">Top Products (Units Sold)</p>
                </div>
                {metrics.top_products.length === 0 ? (
                  <p className="text-[#A1A1A1] text-xs font-mono">No sales yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {metrics.top_products.map((p, idx) => (
                      <li key={p.product_id} className="flex items-center justify-between text-sm" data-testid={`top-product-${idx}`}>
                        <span className="text-[#F2F2F2] truncate flex-1 mr-3">
                          <span className="font-mono text-[#39FF14] mr-2">#{idx + 1}</span>
                          {p.name}
                        </span>
                        <span className="font-mono text-[#39FF14] font-bold">{p.quantity_sold}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Low Stock */}
              <div className="bg-[#0D0D0D] p-5" data-testid="metric-low-stock">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-yellow-400" strokeWidth={1.5} />
                  <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">
                    Inventory Alerts
                    {metrics.inventory.out_of_stock > 0 && (
                      <span className="ml-2 text-red-400">({metrics.inventory.out_of_stock} out of stock)</span>
                    )}
                  </p>
                </div>
                {metrics.inventory.low_stock.length === 0 ? (
                  <p className="text-[#A1A1A1] text-xs font-mono">All inventory healthy ({'>'}=3 units in stock).</p>
                ) : (
                  <ul className="space-y-2">
                    {metrics.inventory.low_stock.map((p) => (
                      <li key={p.id} className="flex items-center justify-between text-sm" data-testid={`low-stock-${p.id}`}>
                        <span className="text-[#F2F2F2] truncate flex-1 mr-3">{p.name}</span>
                        <span className={`font-mono font-bold ${p.stock === 1 ? "text-red-400" : "text-yellow-400"}`}>
                          {p.stock} left
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div ref={formRef} className="bg-[#0D0D0D] border border-[#1A1A1A] p-6 sm:p-8 mb-8">
            <h2 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-6">
              {editProduct ? "Edit Product" : "New Product"}
            </h2>
            <ProductForm
              product={editProduct}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setEditProduct(null); }}
              uploading={uploading}
              setUploading={setUploading}
              categories={categories}
            />
          </div>
        )}

        {/* Error Display */}
        {deleteError && (
          <div className="mb-4 p-4 border border-red-500/30 bg-red-500/10 flex items-center justify-between" data-testid="delete-error">
            <span className="text-red-400 text-sm">{deleteError}</span>
            <button onClick={() => setDeleteError("")} className="text-red-400 hover:text-red-300 ml-4">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap gap-[1px] mb-6">
          {[{ key: "all", label: "All" }, ...categories].map(({ key, label }) => {
            const count = key === "all" ? products.length : products.filter(p => p.category === key).length;
            return (
              <button
                key={key}
                onClick={() => setAdminCategory(key)}
                data-testid={`admin-filter-${key}`}
                className={`px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] font-bold transition-colors ${adminCategory === key ? "bg-[#39FF14] text-[#050505]" : "bg-[#0D0D0D] text-[#A1A1A1] hover:text-[#39FF14] border border-[#1A1A1A]"}`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>

        {/* Products Grid */}
        {(() => {
          const filtered = (adminCategory === "all" ? products : products.filter(p => p.category === adminCategory))
            .slice()
            .sort((a, b) => {
              const aOrder = a.sort_order ?? 1_000_000;
              const bOrder = b.sort_order ?? 1_000_000;
              if (aOrder !== bOrder) return aOrder - bOrder;
              return new Date(b.created_at || 0) - new Date(a.created_at || 0);
            });
          const canReorder = adminCategory !== "all" && filtered.length > 1;
          return filtered.length === 0 && !showForm ? (
            <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-12 text-center" data-testid="no-products-admin">
              <p className="text-[#A1A1A1] mb-4">{products.length === 0 ? "No products yet. Add your first product to get started." : `No products in this category.`}</p>
              {products.length === 0 && (
                <Button onClick={() => setShowForm(true)} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase text-xs font-bold px-6 py-4">
                  <Plus className="h-4 w-4 mr-2" /> Add Product
                </Button>
              )}
            </div>
          ) : (
            <>
              {canReorder && (
                <p className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider mb-3" data-testid="reorder-hint">
                  Tip: Use the ↑/↓ arrows on each product to reorder how they appear in this category for shoppers.
                </p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {filtered.map((p, idx) => (
                  <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-[#0D0D0D] border border-[#1A1A1A] hover:border-[#39FF14]/30 transition-colors group relative">
                    {/* Reorder buttons (only when a single category is selected) */}
                    {canReorder && (
                      <div className="absolute top-2 left-2 z-10 flex flex-col gap-0.5">
                        <button
                          onClick={() => reorderProduct(p.category, p.id, "up")}
                          disabled={idx === 0}
                          title="Move up"
                          data-testid={`move-up-${p.id}`}
                          className="w-6 h-6 flex items-center justify-center bg-[#050505]/80 border border-[#1A1A1A] hover:border-[#39FF14] hover:text-[#39FF14] text-[#A1A1A1] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => reorderProduct(p.category, p.id, "down")}
                          disabled={idx === filtered.length - 1}
                          title="Move down"
                          data-testid={`move-down-${p.id}`}
                          className="w-6 h-6 flex items-center justify-center bg-[#050505]/80 border border-[#1A1A1A] hover:border-[#39FF14] hover:text-[#39FF14] text-[#A1A1A1] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    {/* Image */}
                    <div className="aspect-square overflow-hidden relative">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#0D0D0D] flex items-center justify-center">
                        <span className="font-mono text-[#1A1A1A] text-4xl">X</span>
                      </div>
                    )}
                    <div className={`absolute top-2 right-2 px-1.5 py-0.5 font-mono text-[10px] font-bold ${p.stock > 0 ? "bg-[#39FF14] text-[#050505]" : "bg-red-600 text-white"}`}>
                      {p.stock > 0 ? `${p.stock} in stock` : "OUT"}
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-3">
                    <p className="text-[#F2F2F2] text-sm font-bold truncate mb-1">{p.name}</p>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm text-[#39FF14] font-bold">${p.price?.toFixed(2)}</span>
                      <span className="font-mono text-[10px] text-[#A1A1A1] uppercase">{p.category}</span>
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => { setEditProduct(p); setShowForm(true); setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                        className="flex-1 px-2 py-1.5 border border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] transition-colors font-mono text-[10px] uppercase tracking-wider text-center"
                        data-testid={`edit-product-${p.id}`}
                      >
                        <Pencil className="h-3 w-3 inline mr-1" />Edit
                      </button>
                      {pendingDelete === p.id ? (
                        <div className="flex gap-1">
                          <button onClick={() => handleDelete(p.id)} disabled={deleting} className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white transition-colors font-mono text-[10px] uppercase disabled:opacity-50" data-testid={`confirm-delete-${p.id}`}>
                            {deleting ? "..." : "Yes"}
                          </button>
                          <button onClick={() => setPendingDelete(null)} className="px-1.5 py-1.5 border border-[#1A1A1A] text-[#A1A1A1] hover:text-[#F2F2F2] font-mono text-[10px]" data-testid={`cancel-delete-${p.id}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setPendingDelete(p.id)} className="px-2 py-1.5 border border-[#1A1A1A] hover:border-red-500 text-[#A1A1A1] hover:text-red-500 hover:bg-red-500/10 transition-colors font-mono text-[10px] uppercase" data-testid={`delete-product-${p.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                ))}
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
